// src/components/DropArea/DropArea.jsx
import React, { useCallback, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";

// Defaults (extend as needed)
const ACCEPT_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".svg", ".ai", ".eps", ".psd", ".tif", ".tiff"];
const ACCEPT_MIME = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "application/postscript",
  "application/illustrator",
  "application/vnd.adobe.illustrator",
  "image/vnd.adobe.photoshop",
  "image/tiff",
];
const MAX_FILE_MB = 100;

export const DropArea = ({
  label = "Drop files here or click",
  sublabel,
  disabled = false,
  acceptMimes = ACCEPT_MIME,
  acceptExts = ACCEPT_EXTS,
  multiple = true,
  busy = false,
  onFiles,
  sx,
}) => {
  const [isOver, setIsOver] = useState(false);
  const inputRef = useRef(null);

  // Open native file picker when the area is clicked or activated via keyboard
  const openPicker = () => {
    if (disabled || busy) return;
    const el = inputRef.current;
    if (!el) return;
    // Clear previous value so selecting the SAME file fires onChange again
    el.value = "";
    el.click();
  };

  // Validate a single file against size + mime/ext
  const validFile = (f) => {
    if (!f) return false;
    const sizeOK = f.size <= MAX_FILE_MB * 1024 * 1024;
    const typeOK = !acceptMimes.length || acceptMimes.includes(f.type);
    const ext = (f.name?.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] ?? "");
    const extOK = !acceptExts.length || acceptExts.includes(ext);
    return sizeOK && (typeOK || extOK);
  };

  // Turn a FileList or Array<File> into a filtered array of valid files
  const collect = (list) => {
    const arr = [];
    Array.from(list || []).forEach((f) => {
      if (validFile(f)) arr.push(f);
    });
    return arr;
  };

  // DnD handlers
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    if (disabled || busy) return;

    const dropped = collect(e.dataTransfer?.files);
    if (!dropped.length) {
      toast.error("Unsupported file(s) or too large.");
      return;
    }
    onFiles?.(dropped);
  };

  const handleDrag = (e, over) => {
    if (disabled || busy) return;
    e.preventDefault();
    e.stopPropagation();
    setIsOver(over);
  };

  // Keyboard support (Enter/Space)
  const handleKeyDown = (e) => {
    if (disabled || busy) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  };

  // Input change (click-to-select)
  const handleChange = (e) => {
    if (disabled || busy) return;
    const selected = collect(e.target.files);
    if (!selected.length) {
      toast.error("Unsupported file(s) or too large.");
      return;
    }
    onFiles?.(selected);
    // Clear again so same file can be picked twice in a row later
    e.target.value = "";
  };

  // Optional: paste image/file directly
  const onPaste = useCallback(
    (e) => {
      if (disabled || busy) return;
      const files = Array.from(e.clipboardData?.files || []);
      if (!files.length) return;
      const ok = collect(files);
      if (ok.length) {
        e.preventDefault();
        onFiles?.(ok);
      }
    },
    [disabled, busy, onFiles]
  );

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={(e) => handleDrag(e, true)}
      onDragEnter={(e) => handleDrag(e, true)}
      onDragLeave={(e) => handleDrag(e, false)}
      onKeyDown={handleKeyDown}
      onPaste={onPaste}
      tabIndex={0}
      role="button"
      aria-disabled={disabled || busy}
      onClick={openPicker}
      title={disabled ? "Uploading disabled" : busy ? "Uploading..." : "Click or drop files"}
      sx={{
        position: "relative",
        border: "2px dashed",
        borderColor: isOver ? "#c3171d" : "#c4c4c4",
        bgcolor: isOver ? "rgba(195, 23, 29, 0.05)" : "#fafafa",
        borderRadius: 2,
        p: 2,
        minHeight: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        cursor: disabled || busy ? "not-allowed" : "pointer",
        outline: "none",
        "&:focus-visible": { boxShadow: "0 0 0 3px rgba(195,23,29,0.25)" },
        ...sx,
      }}
    >
      <Box sx={{ display: "grid" }}>
        <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {sublabel ?? `Accepted: ${acceptExts.join(", ")} • up to ${MAX_FILE_MB} MB each`}
        </Typography>
      </Box>

      {/* Hidden input that backs both click + keyboard flows */}
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={multiple}
        accept={[...acceptMimes, ...acceptExts].join(",")}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()} // avoid bubbling weirdness on some browsers
      />

      {busy && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            borderRadius: 2,
          }}
        >
          <CircularProgress size={22} />
          <Typography fontWeight={700}>Uploading…</Typography>
        </Box>
      )}
    </Box>
  );
};

export default DropArea;
